<script setup lang="ts">
  import {useI18n} from 'vue-i18n';
  import {computed, ref} from 'vue';
  import useCpBem from '~/composables/useCpBem';
  import CpButtonRound from '~/components/cp/CpButtonRound/CpButtonRound.vue';
  import CpPillPdp from '~/components/cp/CpPill/CpPillPdp/CpPillPdp.vue';
  import CpText from '~/components/cp/CpText/CpText.vue';
  import CpProductRate from '~/components/cp/CpProductRate/CpProductRate.vue';
  import CpAlert from '~/components/cp/CpAlert/CpAlert.vue';
  import type {ICpArticle} from '~/types';

  const props = defineProps<{
    product: ICpArticle,
    bestSeller?: boolean,
    featured?: boolean,
    productLivesInList?: boolean
  }>();

  const imagesCount = ref('');

  const offerLabel = computed(() => {
    const discount = +props.product.offer.percent || 0;
    return discount - Math.floor(discount) > 0.5 ? Math.ceil(discount) : Math.floor(discount);
  });

  const showGallery = () => {
    navigateTo({name: 'details-gallery', params: {id: props.product.id}});
  };

  const {b, e} = useCpBem('cp-section-pdp-view');
  const {t} = useI18n();

  const emit = defineEmits<{
    (e: 'main-wishlist-btn-pressed', remove: boolean): void,
    (e: 'add-to-another-wishlist'): void,
    (e: 'share'): void,
  }>();

  // @ts-expect-error TODO type swiper
  const slideChange = (args) => {
    const realIndex = args.activeIndex;
    imagesCount.value = (realIndex + 1) + ' / ' + props.product.pictures.length;
  };

  imagesCount.value = (1) + ' / ' + props.product.pictures.length;

  const goToQuestions = () => {
    const mainContent = document.getElementById('main-content');
    const questionsSection = document.getElementById('questions-section');
    if (questionsSection && mainContent) {
      const offset = questionsSection.getBoundingClientRect().top - 64;
      mainContent.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }
  };
  const goToReviews = () => {
    const mainContent = document.getElementById('main-content');
    const reviewsSection = document.getElementById('reviews-section');
    if (reviewsSection && mainContent) {
      const offset = reviewsSection.getBoundingClientRect().top - 64;
      mainContent.scrollTo({
        top: offset,
        behavior: 'smooth',
      });
    }
  };
</script>

<template>
    <div :class="b" data-testid="section-pdp-view">
        <div :class="e('swiper-container')">
            <div :class="e('actions-container')">
                <div :class="e('actions')">
                    <div :class="e('rounded-buttons')">
                        <CpButtonRound :icon="productLivesInList ? 'wishListYes' : 'wishListNo'" :type="productLivesInList ? 'secondary' : 'primary'" @click="emit('main-wishlist-btn-pressed', !!productLivesInList)" />
                        <CpButtonRound icon="wishListActions" @click="emit('add-to-another-wishlist')" />
                        <CpButtonRound icon="share" @click="emit('share')" />
                    </div>
                    <CpPillPdp
                        border="borderNeutral"
                        kind="standard"
                        :label="imagesCount"
                        label-kind="neutral"
                        variant="white"
                    />
                </div>
            </div>
            <div :class="e('pills')">
                <CpPillPdp
                    v-if="product.isNew"
                    kind="standard"
                    label-kind="white"
                    label="Nuevo"
                    variant="mkt"
                />
                <CpPillPdp
                    v-if="bestSeller"
                    kind="standard"
                    label-kind="white"
                    label="Mas vendido"
                    variant="mkt"
                />
                <CpPillPdp
                    v-if="product.eol || product.stockStatus === -1"
                    kind="standard"
                    label-kind="white"
                    label="Agotado"
                    variant="neutralStandard"
                />
                <CpPillPdp
                    v-if="product.isEsd"
                    data-testid="pdp-esd-pill"
                    icon="download"
                    kind="standard"
                    label-kind="white"
                    label="Descargable"
                    variant="primary"
                />
                <CpPillPdp
                    v-if="!product.isDailyOffer && product.isOffer"
                    kind="standard"
                    label-kind="white"
                    :label="`-${offerLabel}%`"
                    variant="mkt"
                />
                <CpPillPdp
                    v-if="product.isDailyOffer && !offerLabel"
                    icon="magic_button"
                    kind="standard"
                    label-kind="white"
                    :label="`Destacado`"
                    variant="mkt"
                />
                <CpPillPdp
                    v-if="product.isDailyOffer && offerLabel"
                    icon="magic_button"
                    kind="standard"
                    label-kind="white"
                    :label="`-${offerLabel}%`"
                    variant="mkt"
                />
            </div>
            <Swiper
                data-testid="product-image"
                :class="e('swiper-container')"
                :loop="true"
                @click="showGallery"
                @slide-change="slideChange"
            >
                <SwiperSlide v-for="(image, index) in product.pictures" :key="image" :class="e('swiper-container')">
                    <div :class="e('image-container')">
                        <img :class="e('image')"
                            :src="image"
                            :alt="product.title"
                            :fetchpriority="index === 0 ? 'high' : 'low'"
                            :loading="index === 0 ? 'eager' : 'lazy'"
                        >
                    </div>
                </SwiperSlide>
            </Swiper>
        </div>
        <div :class="e('text')">
            <CpText variant="heading-h1-pdp" data-testid="pdp-view-product-name">
                {{ product.title }}
                <CpText v-if="product.dne != null" variant="body-bold">
                    ― {{ product.dne }}
                </CpText>
            </CpText>
            <CpText variant="body-regular" type="neutral">
                {{ t('sku') }}{{ product.sku }}
            </CpText>
        </div>
        <CpAlert
            v-if="product.isEsd"
            data-testid="pdp-esd-alert"
            kind="info"
            :close-icon="false"
            :action="false"
        >
            <CpText variant="body-regular">
                {{ t('esdMessage') }}
            </CpText>
        </CpAlert>
        <div :class="e('bottom-pills')">
            <CpProductRate
                v-if="product.rating.total > 0"
                :disabled="product.rating.global === 0"
                :rate="product.rating.global"
                :total-reviews="product.rating.total"
                :hide-count="false"
                on-pdp
                @click="goToReviews()"
            />
            <CpPillPdp
                v-if="product.questionsCount > 0"
                kind="reviews"
                border="borderPrimary"
                label="preguntas"
                label-kind="primary"
                :quantity="product.questionsCount+''"
                variant="white"
                @click="goToQuestions()"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
  @import "assets/style/global";
  .cp-section-pdp-view {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: $cp-gutter-small;
    border-bottom: $cp-border-standard;
    padding: $cp-gutter;
    &__actions {
      position: absolute;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      z-index: 1;
      align-items: center;
      height: 240px;
      left: initial;
    }
    &__pills {
      display: flex;
      flex-direction: row-reverse;
      gap: $cp-gutter-small;
      position: absolute;
      z-index: 1;
    }
    &__rounded-buttons {
      display: flex;
      flex-direction: column;
      gap: $cp-gutter;
    }
    &__image-container {
      display: flex;
      height: 100%;
      align-items: center;
      justify-content: center;
    }
    &__text {
      display: flex;
      flex-direction: column;
      gap: $cp-gutter-xsmall;
    }
    &__bottom-pills {
      display: flex;
      gap: $cp-gutter-small;
    }
    &__actions-container {
      display: flex;
      justify-content: flex-end;
    }
    &__swiper-container {
      height: 240px;
      z-index: 0;
      overflow: hidden;
    }
  }
</style>

<i18n locale="es-US">
  sku: "SKU: "
  esdMessage: "Por favor, considera que productos digitales no se pueden pagar a través de PayPal o tarjeta de crédito."
</i18n>
